import { SxProps, Theme } from "@mui/material";
import { userStatus } from "types/perSeat";
import teamTokens, { teamResponsiveTokens } from "./Team.tokens";
import {
  PHONE_SM_QUERY,
  settingsShellResponsiveTokens,
} from "screens/Settings/Settings.tokens";

const t = teamTokens;
const r = teamResponsiveTokens;

/** Same rhythm `SettingsLayout` puts between its own stacked cards. */
const shellPhone = settingsShellResponsiveTokens.phone;
const stackGap = {
  xs: shellPhone.contentGap.std,
  sm: settingsShellResponsiveTokens.tablet.contentGap,
};

/* ------------------------------------------------------------------ header */

/** Filled "Send Invite" action in the page header. */
export const sendInviteButtonSx: SxProps<Theme> = {
  // Both small references run it full width under the search box.
  width: { xs: "100%", md: "auto" },
  height: { xs: r.action.height, md: t.size.inviteButtonHeight },
  minHeight: { xs: r.action.height, md: t.size.inviteButtonHeight },
  px: { xs: r.action.paddingX, md: t.size.inviteButtonPaddingX },
  py: 0,
  bgcolor: t.color.ctaBg,
  color: t.color.onAccent,
  border: "none",
  borderRadius: t.radius.control,
  fontSize: t.font.inviteButton,
  fontWeight: t.weight.strong,
  lineHeight: 1,
  textTransform: "none",
  boxShadow: "none",
  flexShrink: 0,
  whiteSpace: "nowrap",
  "& .MuiButton-startIcon": {
    m: 0,
    mr: t.space.inviteIconGap,
  },
  "&:hover": { bgcolor: t.color.ctaBgHover, boxShadow: "none" },
  // The design swaps the filled action for a flat, outlined "all seats taken"
  // state rather than dimming it.
  "&.Mui-disabled": {
    bgcolor: t.color.disabledBg,
    color: t.color.disabledText,
    border: `${t.border.hairline} solid ${t.color.disabledBorder}`,
    opacity: 1,
  },
};

/* ------------------------------------------------------------- seats card */

/** White rounded card holding the seat meter and its action. */
export const seatsCardSx: SxProps<Theme> = {
  // The reference opens the phone/tablet screen with the seat meter, above the
  // search box and "Send Invite"; see `headerOrder` on `SettingsLayout`.
  order: { xs: 1, md: 0 },
  display: "flex",
  // The reference stacks the meter over a full-width "Manage seats" below `md`.
  flexDirection: { xs: "column", md: "row" },
  alignItems: { xs: "stretch", md: "center" },
  gap: { xs: r.seats.gap, md: t.space.seatsGap },
  px: { xs: "16px", md: t.space.seatsPaddingX },
  py: { xs: "15px", md: t.space.seatsPaddingY },
  bgcolor: t.color.surface,
  border: `${t.border.hairline} solid ${t.color.cardBorder}`,
  borderRadius: t.radius.card,
  boxShadow: t.shadow.card,
  boxSizing: "border-box",
};

export const seatsMeterColumnSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: t.space.seatsStackGap,
  flex: 1,
  minWidth: 0,
};

/** "Seats · N of M purchased seats in use · N seats available". */
export const seatsHeadlineSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "baseline",
  // The usage sentence is longer than the reference's "6 of 10 used", so it is
  // allowed to wrap rather than squeeze the "N free" note off the card.
  flexWrap: { xs: "wrap", md: "nowrap" },
  gap: { xs: r.seats.inlineGap, md: t.space.seatsInlineGap },
};

export const seatsLabelSx: SxProps<Theme> = {
  fontSize: t.font.seatsLabel,
  fontWeight: t.weight.strong,
  color: t.color.ink,
  lineHeight: "normal",
};

export const seatsUsageSx: SxProps<Theme> = {
  fontSize: t.font.seatsUsage,
  fontWeight: t.weight.medium,
  color: t.color.muted,
  lineHeight: "normal",
};

export const seatsNoteSx: SxProps<Theme> = {
  ml: "auto",
  fontSize: t.font.seatsNote,
  fontWeight: t.weight.regular,
  color: t.color.columnHeader,
  lineHeight: "normal",
  whiteSpace: "nowrap",
};

export const seatsTrackSx: SxProps<Theme> = {
  height: t.size.seatTrackHeight,
  borderRadius: t.radius.pill,
  bgcolor: t.color.seatTrack,
  overflow: "hidden",
};

/** Meter fill; turns red once every purchased seat is taken. */
/** Fill turns amber from 80% of the allowance and red once it's full. */
export const seatsFillSx = (
  percent: number,
  isFull: boolean,
): SxProps<Theme> => ({
  width: `${percent}%`,
  height: t.size.seatTrackHeight,
  borderRadius: t.radius.pill,
  bgcolor: isFull
    ? t.color.seatFillFull
    : percent >= 80
      ? t.color.seatFillWarning
      : t.color.accent,
});

/** Per-seat strapline under the meter ("$9.00 / seat · billed yearly · …"). */
export const seatsPerSeatNoteSx: SxProps<Theme> = {
  fontSize: t.font.seatsNote,
  fontWeight: t.weight.regular,
  color: t.color.columnHeader,
  lineHeight: "normal",
};

/**
 * "Manage seats" / "Upgrade plan" — a plain text link, not a button: it never
 * does anything on this page, only sends the admin to the plan screen, so it
 * shouldn't carry the same weight as an on-page action.
 */
export const manageSeatsButtonSx: SxProps<Theme> = {
  minWidth: "unset",
  height: t.size.manageButtonHeight,
  minHeight: t.size.manageButtonHeight,
  px: t.space.seatsInlineGap,
  py: 0,
  bgcolor: "transparent",
  color: t.color.accent,
  border: "none",
  fontSize: t.font.manageButton,
  fontWeight: t.weight.strong,
  lineHeight: 1,
  textTransform: "none",
  textDecoration: "none",
  boxShadow: "none",
  flexShrink: 0,
  whiteSpace: "nowrap",
  "&:hover": {
    bgcolor: "transparent",
    color: t.color.accentHover,
    textDecoration: "underline",
    boxShadow: "none",
  },
};

/* ------------------------------------------------------------- table card */

/** White rounded card that wraps the whole member table. */
export const tableCardSx: SxProps<Theme> = {
  order: { xs: 3, md: 0 },
  /*
    Below `md` the reference drops the wrapper: every member is their own card
    and the pager sits loose underneath, straight on the page canvas.
  */
  bgcolor: { xs: "transparent", md: t.color.surface },
  border: {
    xs: "none",
    md: `${t.border.hairline} solid ${t.color.cardBorder}`,
  },
  borderRadius: { xs: 0, md: t.radius.card },
  boxShadow: { xs: "none", md: t.shadow.card },
  overflow: { xs: "visible", md: "hidden" },
  display: { xs: "flex", md: "block" },
  flexDirection: "column",
  gap: stackGap,
  [PHONE_SM_QUERY]: { gap: shellPhone.contentGap.sm },
};

/** Horizontal scroller: the six fixed columns stop fitting on narrow viewports. */
export const tableScrollSx: SxProps<Theme> = {
  overflowX: { xs: "visible", md: "auto" },
  display: { xs: "contents", md: "block" },
  scrollbarWidth: "thin",
  "&::-webkit-scrollbar": { height: t.size.tableScrollbarHeight },
  "&::-webkit-scrollbar-track": { background: "transparent" },
  "&::-webkit-scrollbar-thumb": {
    background: t.color.scrollbarThumb,
    borderRadius: t.size.tableScrollbarRadius,
  },
};

export const tableInnerSx: SxProps<Theme> = {
  minWidth: { xs: 0, md: t.size.tableMinWidth },
};

/** Grey "NAME / PHONE / EMAIL / ROLE / STATUS" strip. */
export const columnHeaderSx: SxProps<Theme> = {
  // Six fixed tracks the phone cannot carry; the rows become cards below `md`,
  // so the strip goes with them.
  display: { xs: "none", md: "grid" },
  gridTemplateColumns: t.grid.row,
  px: t.space.gutter,
  py: t.space.columnHeaderY,
  bgcolor: t.color.headerBg,
  borderBottom: `${t.border.hairline} solid ${t.color.headerBorder}`,
  fontSize: t.font.columnHeader,
  fontWeight: t.weight.strong,
  letterSpacing: t.letterSpacing.columnHeader,
  color: t.color.columnHeader,
  lineHeight: "normal",
};

export const visuallyHiddenSx: SxProps<Theme> = {
  position: "absolute",
  width: "1px",
  height: "1px",
  p: 0,
  m: "-1px",
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
  border: 0,
};

/** A single member row. The last row drops its divider. */
export const rowSx = (isLast: boolean): SxProps<Theme> => ({
  display: "grid",
  gridTemplateColumns: t.grid.row,
  alignItems: "center",
  px: t.space.gutter,
  py: t.space.rowY,
  borderBottom: isLast
    ? "none"
    : `${t.border.hairline} solid ${t.color.rowDivider}`,
  fontSize: t.font.cell,
  color: t.color.ink,
  lineHeight: "normal",
});

export const nameCellSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: t.space.nameGap,
  minWidth: 0,
};

/**
 * The same box `avatarSx` occupies, for the placeholder that stands in for it.
 * The avatar — not the text beside it — is what sets a row's height, so the
 * loading rows need it to match the rows that replace them.
 */
export const skeletonAvatarSx: SxProps<Theme> = {
  width: t.size.avatar,
  height: t.size.avatar,
  flexShrink: 0,
};

export const avatarSx: SxProps<Theme> = {
  width: t.size.avatar,
  height: t.size.avatar,
  borderRadius: t.radius.avatar,
  bgcolor: t.color.avatarBg,
  color: t.color.avatarText,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: t.font.avatar,
  fontWeight: t.weight.strong,
  lineHeight: "normal",
  flexShrink: 0,
};

export const nameTextSx: SxProps<Theme> = {
  fontWeight: t.weight.medium,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

export const phoneCellSx: SxProps<Theme> = {
  color: t.color.muted,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

export const emailCellSx: SxProps<Theme> = {
  color: t.color.muted,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  pr: t.space.emailPaddingRight,
};

export const rolePillSx: SxProps<Theme> = {
  display: "inline-flex",
  alignItems: "center",
  fontSize: t.font.rolePill,
  fontWeight: t.weight.medium,
  color: t.color.rolePillText,
  bgcolor: t.color.rolePillBg,
  px: "11px",
  py: "3px",
  borderRadius: t.radius.pill,
  lineHeight: "normal",
  whiteSpace: "nowrap",
  maxWidth: "100%",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

/** STATUS text; the design only fixes the "Active" green, so the other two
 *  states reuse the palette the design uses elsewhere for locked / inactive. */
export const statusSx = (status: string): SxProps<Theme> => {
  const color =
    status === userStatus.active
      ? t.color.statusActive
      : status === userStatus.locked
        ? t.color.statusLocked
        : status === userStatus.pendingVerification
          ? t.color.statusInvited
          : t.color.statusInactive;

  return {
    fontSize: t.font.status,
    fontWeight: t.weight.strong,
    color,
    lineHeight: "normal",
  };
};

/** Row overflow trigger — the design draws it as a bold "⋮" glyph. */
export const actionButtonSx: SxProps<Theme> = {
  font: "inherit",
  display: "block",
  width: "100%",
  p: 0,
  m: 0,
  border: "none",
  bgcolor: "transparent",
  textAlign: "right",
  color: t.color.actionGlyph,
  fontWeight: t.weight.strong,
  letterSpacing: t.letterSpacing.action,
  lineHeight: "normal",
  cursor: "pointer",
  "&:hover": { color: t.color.ink },
  "&:focus-visible": {
    outline: `2px solid ${t.color.accent}`,
    outlineOffset: "2px",
    borderRadius: "3px",
  },
};

/** Placeholder shown in place of rows when no member matches. */
export const emptyStateSx: SxProps<Theme> = {
  px: t.space.gutter,
  py: t.space.emptyY,
  textAlign: "center",
  fontSize: t.font.empty,
  fontWeight: t.weight.regular,
  color: t.color.emptyText,
  lineHeight: "normal",
};

/** Footer strip hosting the pager. */
export const paginationBarSx: SxProps<Theme> = {
  display: "flex",
  // Centred under the cards below `md` rather than closing a card.
  justifyContent: { xs: "center", md: "flex-end" },
  alignItems: "center",
  px: { xs: 0, md: t.space.gutter },
  py: { xs: r.pager.paddingY, md: t.space.footerY },
  borderTop: {
    xs: "none",
    md: `${t.border.hairline} solid ${t.color.headerBorder}`,
  },
};

/** MUI <Pagination> restyled to the design's 28px square items. */
export const paginationSx: SxProps<Theme> = {
  "& .MuiPagination-ul": {
    gap: { xs: r.pager.gap, md: t.space.paginationGap },
    flexWrap: "nowrap",
  },
  "& .MuiPagination-ul > li": { m: 0 },
  "& .MuiPaginationItem-root": {
    minWidth: { xs: r.pager.itemSize, md: t.size.pageItem },
    width: { xs: r.pager.itemSize, md: t.size.pageItem },
    height: { xs: r.pager.itemSize, md: t.size.pageItem },
    m: 0,
    p: 0,
    borderRadius: { xs: r.pager.itemRadius, md: t.radius.pageItem },
    bgcolor: "transparent",
    border: {
      xs: `${t.border.hairline} solid ${r.pager.idleBorder}`,
      md: "none",
    },
    fontSize: { xs: r.pager.fontSize, md: t.font.pageItem },
    fontWeight: t.weight.medium,
    color: t.color.pageItem,
    "&:hover": { bgcolor: t.color.pageHoverFill },
  },
  "& .MuiPaginationItem-root.Mui-focusVisible": {
    outline: "2px solid",
    outlineColor: "primary.main",
    outlineOffset: "-2px",
  },
  "& .MuiPaginationItem-root.Mui-selected": {
    bgcolor: t.color.ctaBg,
    color: t.color.onAccent,
    border: "none",
    fontWeight: t.weight.strong,
    "&:hover": { bgcolor: t.color.ctaBg, color: t.color.onAccent },
  },
  "& .MuiPaginationItem-ellipsis": {
    width: "auto",
    minWidth: "unset",
    px: t.space.paginationGap,
    height: t.size.pageItem,
    display: "flex",
    alignItems: "center",
    color: t.color.columnHeader,
    bgcolor: "transparent",
    fontSize: t.font.pageItem,
    fontWeight: t.weight.regular,
  },
  "& .MuiPaginationItem-icon": {
    width: t.size.pageArrow,
    height: t.size.pageArrow,
    fontSize: t.size.pageArrow,
  },
  "& .MuiPaginationItem-root.Mui-disabled": {
    color: t.color.pageDisabled,
    opacity: 1,
  },
};

/* ---------------------------------------------------------------- card list */

/**
 * Below `md` a member is a card: monogram, then their name and status, email,
 * phone and role pill stacked beside it, with the overflow trigger trailing.
 */
export const memberCardSx: SxProps<Theme> = {
  display: "flex",
  boxSizing: "border-box",
  alignItems: "flex-start",
  gap: r.card.gap,
  p: r.card.padding,
  bgcolor: t.color.surface,
  border: `${t.border.hairline} solid ${r.card.border}`,
  borderRadius: r.card.radius,
};

export const memberCardAvatarSx: SxProps<Theme> = {
  width: r.card.avatar,
  height: r.card.avatar,
  flexShrink: 0,
  borderRadius: t.radius.avatar,
  bgcolor: t.color.avatarBg,
  color: t.color.avatarText,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: r.card.avatarFontSize,
  fontWeight: t.weight.strong,
  lineHeight: "normal",
};

export const memberCardTextSx: SxProps<Theme> = {
  flex: 1,
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  gap: r.card.textGap,
};

export const memberCardNameRowSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: r.card.nameGap,
  flexWrap: "wrap",
};

export const memberCardNameSx: SxProps<Theme> = {
  fontSize: r.card.nameFontSize,
  fontWeight: t.weight.strong,
  color: t.color.ink,
  lineHeight: "normal",
  wordBreak: "break-word",
};

export const memberCardEmailSx: SxProps<Theme> = {
  fontSize: r.card.emailFontSize,
  color: t.color.muted,
  lineHeight: "normal",
  wordBreak: "break-all",
};

export const memberCardPhoneSx: SxProps<Theme> = {
  fontSize: r.card.phoneFontSize,
  color: t.color.columnHeader,
  lineHeight: "normal",
};

export const memberCardPillSx: SxProps<Theme> = {
  alignSelf: "flex-start",
  display: "inline-flex",
  alignItems: "center",
  maxWidth: "100%",
  p: r.card.pillPadding,
  borderRadius: t.radius.pill,
  bgcolor: t.color.rolePillBg,
  color: t.color.rolePillText,
  fontSize: r.card.pillFontSize,
  fontWeight: t.weight.medium,
  lineHeight: "normal",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

/** Overflow trigger, trailing the card. */
export const memberCardActionSx: SxProps<Theme> = {
  font: "inherit",
  flexShrink: 0,
  p: 0,
  m: 0,
  border: "none",
  bgcolor: "transparent",
  color: t.color.actionGlyph,
  fontWeight: t.weight.strong,
  letterSpacing: t.letterSpacing.action,
  lineHeight: "normal",
  cursor: "pointer",
  "&:hover": { color: t.color.ink },
  "&:focus-visible": {
    outline: `2px solid ${t.color.accent}`,
    outlineOffset: "2px",
    borderRadius: "3px",
  },
};
